import { create } from 'zustand';
import { bannerDismissalService } from '@/services';
import type {
  BannerDismissalRow,
  UpsertDismissalInput,
  RemoveDismissalInput,
} from '@/services/banner-dismissal.service';

interface BannerDismissalsState {
  dismissals: BannerDismissalRow[];
  loaded: boolean;
  loading: boolean;
  mutating: boolean;
  error: Error | null;
  load: () => Promise<void>;
  upsert: (input: UpsertDismissalInput) => Promise<void>;
  remove: (input: RemoveDismissalInput) => Promise<void>;
}

export const useBannerDismissalsStore = create<BannerDismissalsState>(
  (set, get) => ({
    dismissals: [],
    loaded: false,
    loading: false,
    mutating: false,
    error: null,

    load: async () => {
      // Dedupe: if already loaded or loading, skip.
      if (get().loaded || get().loading) return;
      set({ loading: true, error: null });
      try {
        const rows = await bannerDismissalService.listDismissals();
        set({ dismissals: rows, loaded: true, loading: false });
      } catch (err) {
        set({
          error: err instanceof Error ? err : new Error(String(err)),
          loading: false,
        });
      }
    },

    upsert: async (input) => {
      if (get().mutating) return;
      set({ mutating: true });
      try {
        await bannerDismissalService.upsertDismissal(input);
        const rows = await bannerDismissalService.listDismissals();
        set({ dismissals: rows, loaded: true });
      } finally {
        set({ mutating: false });
      }
    },

    remove: async (input) => {
      if (get().mutating) return;
      set({ mutating: true });
      try {
        await bannerDismissalService.removeDismissal(input);
        const rows = await bannerDismissalService.listDismissals();
        set({ dismissals: rows, loaded: true });
      } finally {
        set({ mutating: false });
      }
    },
  }),
);
